import React, { useContext, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import bilAPI from "../../services/api";
import { joinQuestionFolder } from "../../services/questionService";
import { UserContext } from "../../utils/contexts/UserContext";
import { canOpenPage, getHomePage } from "../../utils/permissions";
import { getApiErrorMessage } from "../../utils/apiError";
import SimpleLoader from "../../components/Loader/SimpleLoader";

/**
 * A link to a folder, /questions/folders/:folderUuid, as "Share" copies it. With ?invite=<token> it first
 * joins the subject by the link (POST question_folders/join/) and reloads the account, whose folder_roles now
 * open the bank; then it opens the folder in the bank. Without an invite it only works for members. Logged
 * out, it goes to the login page, which comes back here.
 */
export default function FolderLink() {
  const [t] = useTranslation();
  const { user, setUser, isLoading } = useContext(UserContext);
  const { folderUuid } = useParams();
  const [searchParams] = useSearchParams();
  const invite = searchParams.get("invite");
  const location = useLocation();
  const navigate = useNavigate();
  const [joining, setJoining] = useState(Boolean(invite));
  // One join per token: the effect runs twice in development (StrictMode).
  const joinedRef = useRef(null);

  useEffect(() => {
    if (!invite || !user?.uuid || joinedRef.current === invite) return;
    joinedRef.current = invite;
    setJoining(true);
    joinQuestionFolder(invite)
      .then(async (res) => {
        const account = await bilAPI.get("/auth/my_account/").catch(() => null);
        if (account?.data) setUser(account.data);
        toast.success(
          t("folders.invite.joined", { name: res?.folder?.name || "", role: t(`folders.roles.${res?.role}`, { defaultValue: res?.role || "" }) })
        );
        navigate(`/questions?folder=${res?.folder?.uuid || folderUuid}`, { replace: true });
      })
      .catch((err) => {
        toast.error(getApiErrorMessage(t, err, "folders.invite.failed"));
        navigate(`/questions/folders/${folderUuid}`, { replace: true });
      })
      .finally(() => setJoining(false));
  }, [invite, user?.uuid, folderUuid, navigate, setUser, t]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <SimpleLoader className="h-12" />
      </div>
    );
  }
  if (!user?.uuid) return <Navigate to="/login" replace state={{ from: location }} />;
  if (invite || joining) {
    return (
      <div role="status" className="flex h-full flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <SimpleLoader className="h-10" />
        {t("folders.invite.joining")}
      </div>
    );
  }
  if (!canOpenPage(user, "/questions")) return <Navigate to={getHomePage(user)} replace />;
  return <Navigate to={`/questions?folder=${folderUuid}`} replace />;
}
